import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { TabSwitcher } from '@/components/ui//app/tab-switcher';
import { Input } from '@/components/ui/input';
import { BackButton } from '@/components/ui/app/back-button';
import { Checkbox } from '@/components/ui/checkbox';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils/validation-errors';
import { useTheme } from '@/lib/contexts/theme-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { useTranslation } from 'react-i18next';

export function RegisterForm({ onBack, onSwitchToLogin }) {
    const { t } = useTranslation();
    const { themeSlug } = useTheme();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { setIsAuthenticated } = useAuth();
    const isHorodateme = theme.slug === 'horodateme';
    const [accountType, setAccountType] = useState('personal');
    const [acceptOffers, setAcceptOffers] = useState(false);
    const [acceptCgu, setAcceptCgu] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [companySiret, setCompanySiret] = useState('');
    const [companyVat, setCompanyVat] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const requiresCompanyLegalIds = themeSlug === 'horodateme';

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password || !confirmPassword) {
            toast.error(t('welcome:veuillezRemplirTousLesChampsObligatoires'));
            return;
        }

        if (password !== confirmPassword) {
            toast.error(t('welcome:lesMotsDePasseNeCorrespondentPas'));
            return;
        }

        if (accountType === 'professional' && !companyName.trim()) {
            toast.error(t('welcome:leNomDeLEntrepriseEstObligatoire'));
            return;
        }

        if (accountType === 'professional' && requiresCompanyLegalIds) {
            if (!companySiret.trim()) {
                toast.error(t('welcome:leNumeroSiretEstObligatoire'));
                return;
            }

            if (!companyVat.trim()) {
                toast.error(t('welcome:leNumeroDeTvaIntracommunautaireEstObligatoire'));
                return;
            }
        }

        if (!acceptCgu) {
            toast.error(t('welcome:vousDevezAccepterLesConditionsGeneralesDUtilisat'));
            return;
        }

        setIsSubmitting(true);
        try {
            if (accountType === 'professional') {
                await horodatyApi.registerPro({
                    email,
                    password,
                    confirm_password: confirmPassword,
                    company: {
                        name: companyName.trim(),
                        ...(companySiret.trim() ? { siret: companySiret.trim() } : {}),
                        ...(companyVat.trim() ? { vat: companyVat.trim() } : {}),
                    },
                    auto_login: true,
                });
            } else {
                await horodatyApi.register({
                    email,
                    password,
                    confirm_password: confirmPassword,
                    auto_login: true,
                });
            }

            // PHP stored token in session via auto_login → mettre à jour l'état d'auth côté React
            setIsAuthenticated(true);
            toast.success(t('welcome:compteCreeAvecSucces'));
            navigate('/dashboard');
        } catch (error) {
            const message = getErrorMessage(error, t('welcome:registerFailed'));
            toast.error(message.split('\n')[0]); // Show first error in toast
        } finally {
            setIsSubmitting(false);
        }
    };

    const accountTabs = [
        { label: t('welcome:personnel'), value: 'personal' },
        { label: t('welcome:professionnel'), value: 'professional' },
    ];

    return (
        <div className="relative flex flex-col min-h-full">
            <div className="shrink-0">
                <BackButton onClick={onBack} />
            </div>

            <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full py-8">
                <h2 className="text-4xl font-black mb-8 text-center">{t('welcome:creerUnCompte')}</h2>

                <TabSwitcher tabs={accountTabs} activeTab={accountType} onChange={setAccountType} className="mb-8" />

                <form className="w-full space-y-4" onSubmit={handleSubmit}>
                    <Input
                        type="email"
                        placeholder={t('welcome:email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    {accountType === 'professional' ? (
                        <>
                            <Input
                                type="text"
                                placeholder={t('welcome:nomDeLEntreprise')}
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                required
                            />
                            {requiresCompanyLegalIds ? (
                                <>
                                    <Input
                                        type="text"
                                        placeholder={t('welcome:siret')}
                                        value={companySiret}
                                        onChange={(e) =>
                                            setCompanySiret(e.target.value.replace(/\D/g, '').slice(0, 14))
                                        }
                                        required
                                    />
                                    <Input
                                        type="text"
                                        placeholder={t('welcome:nTvaIntracommunautaire')}
                                        value={companyVat}
                                        onChange={(e) => setCompanyVat(e.target.value.toUpperCase().slice(0, 20))}
                                        required
                                    />
                                </>
                            ) : null}
                        </>
                    ) : null}
                    <div>
                        <Input
                            type="password"
                            placeholder={t('welcome:motDePasse')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <p className="text-xs text-muted-foreground mt-2 px-2">
                            {t('welcome:auMoins8CaracteresIncluantUneMajusculeUne')}
                        </p>
                    </div>
                    <Input
                        type="password"
                        placeholder={t('welcome:confirmerLeMotDePasse')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />

                    <label className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                            checked={acceptOffers}
                            onCheckedChange={setAcceptOffers}
                            className="h-5 w-5 rounded border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <span className="text-sm text-muted-foreground">
                            {t('welcome:jAccepteDeRecevoirDesOffresPromotionnelles')}
                        </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                            checked={acceptCgu}
                            onCheckedChange={setAcceptCgu}
                            className="mt-0.5 h-5 w-5 rounded border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <span className="text-sm text-muted-foreground">
                            {t('welcome:jAccepteLes')}{' '}
                            <a
                                href="https://horodaty.fr/mentions-legales/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-secondary underline hover:opacity-80 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('welcome:conditionsGeneralesDUtilisation')}
                            </a>{' '}
                            *
                        </span>
                    </label>

                    <SubmitButton
                        isSubmitting={isSubmitting}
                        loadingText={t('welcome:creation')}
                        color="secondary"
                        size="cta"
                        className="w-full mt-6"
                    >
                        {t('welcome:creerMonCompte')}
                    </SubmitButton>
                </form>

                <p className="mt-6 text-muted-foreground">
                    {t('welcome:dejaUnCompte')}{' '}
                    <Button
                        variant="link"
                        color="secondary"
                        onClick={onSwitchToLogin}
                        className="font-medium p-0 h-auto"
                    >
                        {t('welcome:seConnecter')}
                    </Button>
                </p>
            </div>
        </div>
    );
}
